/**
 * evidenceViews — pure helpers for the run-record page's Evidence section
 * (26742), "All · chronological" tab.
 *
 * The tab tags every step with who acted on it. There is no first-class
 * "actor" field on a chronology row today — it is derived: a step reads as
 * `"servicer"` iff a HITL event with a non-null `answered_by` names that same
 * step; every other step (RPA/system steps, and HITL steps nobody has
 * answered yet) reads as `"ora"`.
 */
import type { ChronologyEntry } from "../types";
import type { HitlEvent } from "../components/WorkflowHitlHistorySection";

export type StepActor = "servicer" | "ora";

/**
 * Who acted on a given step.
 *
 * `stepId` wins when supplied; `chronologyEntry.step_id` is the fallback so a
 * caller iterating chronology rows can pass the row itself without also
 * threading its `step_id` through separately. Neither present, or no HITL
 * event answered by a person names the resolved id → `"ora"`.
 */
export function actorForStep(
  stepId: string | null | undefined,
  chronologyEntry: ChronologyEntry | null | undefined,
  hitlEvents: HitlEvent[] | null | undefined,
): StepActor {
  const resolvedStepId = stepId ?? chronologyEntry?.step_id ?? null;
  if (!resolvedStepId || !hitlEvents || hitlEvents.length === 0) return "ora";
  const answeredByPerson = hitlEvents.some(
    (event) =>
      event.step_id === resolvedStepId &&
      event.answered_by != null &&
      event.answered_by.trim().length > 0,
  );
  return answeredByPerson ? "servicer" : "ora";
}

export default actorForStep;
